import { useRef, useEffect, useCallback } from 'react';
import anamConfig from '../config/anamConfig';

const WAV_HEADER_BYTES = 44;
const CONVAI_SAMPLE_RATE = 44100;

/**
 * Utility to convert a Base64 string into a Uint8Array efficiently.
 */
function base64ToUint8Array(base64) {
  try {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  } catch (err) {
    console.error('[Anam] Failed to decode Base64 audio chunk:', err);
    return null;
  }
}

/**
 * Every ConvAI audio chunk is a self-contained WAV file (44-byte RIFF header
 * followed by PCM), not just the first. Strip the header from every chunk so
 * only raw PCM reaches Anam; otherwise the header bytes are played as audio,
 * producing a beep after each chunk.
 */
function extractPcmFromConvaiChunk(u8) {
  if (!u8 || u8.byteLength <= WAV_HEADER_BYTES) return null;
  return u8.subarray(WAV_HEADER_BYTES);
}

function createS16LEResampler(srcRate, dstRate) {
  const ratio = srcRate / dstRate;
  let phase = 0;
  let pendingByte = null;
  let lastSrcSample = 0;

  function resample(pcmU8) {
    if (!pcmU8 || pcmU8.byteLength === 0) return new Uint8Array(0);

    let workU8 = pcmU8;
    if (pendingByte !== null) {
      const merged = new Uint8Array(1 + pcmU8.byteLength);
      merged[0] = pendingByte;
      merged.set(pcmU8, 1);
      workU8 = merged;
      pendingByte = null;
    }

    let evenByteLength = workU8.byteLength;
    if (evenByteLength % 2 !== 0) {
      pendingByte = workU8[evenByteLength - 1];
      evenByteLength -= 1;
    }

    if (evenByteLength === 0) return new Uint8Array(0);

    const src = new Int16Array(
      workU8.buffer,
      workU8.byteOffset,
      evenByteLength / 2
    );
    const srcLen = src.length;

    const outLength = Math.max(0, Math.floor((srcLen - phase) / ratio));
    if (outLength === 0) {
      lastSrcSample = src[srcLen - 1] ?? lastSrcSample;
      phase -= srcLen;
      if (phase < 0) phase = 0;
      return new Uint8Array(0);
    }

    const dst = new Int16Array(outLength);

    for (let i = 0; i < outLength; i++) {
      const srcIdx = phase;
      const lo = Math.floor(srcIdx);
      const hi = lo + 1;
      const frac = srcIdx - lo;

      const loSample = lo < srcLen ? src[lo] : lastSrcSample;
      const hiSample = hi < srcLen ? src[hi] : lastSrcSample;

      dst[i] = (loSample * (1 - frac) + hiSample * frac) | 0;

      phase += ratio;
    }

    lastSrcSample = src[srcLen - 1];
    phase -= srcLen;
    if (phase < 0) phase = 0;

    return new Uint8Array(dst.buffer, dst.byteOffset, dst.byteLength);
  }

  function reset() {
    phase = 0;
    pendingByte = null;
    lastSrcSample = 0;
  }

  return { resample, reset };
}

/**
 * ── MODE A: Auto-streaming ────────────────────────────────────────
 * Chunks are processed and forwarded to Anam as they arrive from ConvAI,
 * keeping latency minimal. Use when you don't need to gate audio on text.
 *
 * enqueueChunk(b64)      — convert + send chunk immediately via serial queue
 * enqueueEndSequence()   — signal end of sequence after last chunk
 * flushQueue()           — discard all pending work (interrupt / error)
 *
 * ── MODE B: Staged commit ──────────────────────────────────────────────
 * Chunks are held in a raw staging buffer (no conversion, no sending) until
 * commitStagedChunks() is explicitly called. This lets endOfAiResponse()
 * inspect the full aiTranscriptRef first and decide whether to send or discard
 * the audio — keeping text and audio decisions synchronized.
 *
 * stageChunk(b64)        — hold decoded u8 in staging buffer, no side-effects
 * commitStagedChunks()   — convert + enqueue all staged chunks in order,
 * then enqueue handleEndAudioSequence at the tail
 * discardStagedChunks()  — drop staged buffer without sending anything
 * (use when containsMessageNotSent → true)
 * ── Current Mode: Mode B ────────────────────────────────────────────────────
 * ConvAI streams text and audio simultaneously. Both are accumulated during
 * the response — aiTranscriptRef for text, the staging buffer for audio.
 * Nothing is sent to Anam until getEndOfResponse() fires and
 * endOfAiResponse() runs the containsMessageNotSent() check on the full
 * transcript.
 */
export function useAnamConvaiAudioQueue({
  handleSendAudioChunk,
  handleEndAudioSequence
}) {
  const queueRef = useRef(Promise.resolve());
  const isMountedRef = useRef(true);
  const stagedChunksRef = useRef([]);
  const resamplerRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      queueRef.current = Promise.resolve();
    };
  }, []);

  if (!resamplerRef.current) {
    resamplerRef.current = createS16LEResampler(
      CONVAI_SAMPLE_RATE,
      anamConfig.sampleRate
    );
  }

  /**
   * Convert a single raw ConvAI Uint8Array to resampled PCM and send it.
   * Called from both modes — streaming directly or replaying staged chunks.
   */
  const processChunk = useCallback(
    (u8) => {
      if (!u8 || u8.byteLength === 0) return;
      if (!isMountedRef.current) return;

      try {
        const pcmRaw = extractPcmFromConvaiChunk(u8);
        if (!pcmRaw || pcmRaw.byteLength === 0) return;

        const pcmU8 = resamplerRef.current.resample(pcmRaw);
        if (pcmU8.byteLength > 0) {
          handleSendAudioChunk?.(pcmU8);
        }
      } catch (err) {
        console.error('[Anam] processChunk error:', err);
      }
    },
    [handleSendAudioChunk]
  );

  // Mode A: Auto-streaming API

  /**
   * Decode Base64, convert, and forward a ConvAI audio chunk to Anam immediately via the
   * serial queue. Call this inside convaiResponseCallback on each chunk.
   */
  const enqueueChunk = useCallback(
    (b64) => {
      if (!b64 || b64.length === 0) return;

      const u8 = base64ToUint8Array(b64);
      if (!u8) return;

      queueRef.current = queueRef.current.then(() => {
        processChunk(u8);
      });
    },
    [processChunk]
  );

  /**
   * Enqueue the Anam end-of-sequence signal after all streamed chunks.
   * Call at endOfAiResponse() after all enqueueChunk() calls.
   */
  const enqueueEndSequence = useCallback(() => {
    queueRef.current = queueRef.current.then(() => {
      if (!isMountedRef.current) return;
      handleEndAudioSequence?.();
    });
  }, [handleEndAudioSequence]);

  /**
   * Immediately reset the serial queue and chunk state.
   * Use on interrupt, timeout, or error to prevent stale audio leaking into
   * the next response. Drops all in-flight work without awaiting completion.
   */
  const flushQueue = useCallback(() => {
    queueRef.current = Promise.resolve();
    resamplerRef.current.reset();
  }, []);

  // Mode B: Staged-commit API

  /**
   * Decode Base64 and hold the raw ConvAI audio chunk bytes in the staging
   * buffer without converting or sending it yet.
   *
   * Called inside convaiResponseCallback in place of enqueueChunk() when
   * you want to gate sending on the text transcript check in endOfAiResponse.
   */
  const stageChunk = useCallback((b64) => {
    if (!b64 || b64.length === 0) return;

    const u8 = base64ToUint8Array(b64);
    if (!u8) return;

    stagedChunksRef.current.push(u8);
  }, []);

  /**
   * Drain the staging buffer into the serial queue in arrival order, then
   * append handleEndAudioSequence at the tail.
   *
   * Called in endOfAiResponse() after the text transcript check passes.
   * All staged chunks are processed synchronously within the queue — no gap
   * between commit and Anam receiving the first byte.
   *
   * Resets staging state so the next response starts clean.
   */
  const commitStagedChunks = useCallback(() => {
    const staged = stagedChunksRef.current;

    if (staged.length === 0) {
      queueRef.current = queueRef.current.then(() => {
        if (!isMountedRef.current) return;
        handleEndAudioSequence?.();
      });
      return;
    }

    for (const u8 of staged) {
      // Capture loop var for the closure
      const capturedU8 = u8;
      queueRef.current = queueRef.current.then(() => {
        processChunk(capturedU8);
      });
    }

    queueRef.current = queueRef.current.then(() => {
      if (!isMountedRef.current) return;
      handleEndAudioSequence?.();
    });

    // Clear staging buffer immediately — the closures above hold their own refs.
    stagedChunksRef.current = [];
  }, [processChunk, handleEndAudioSequence]);

  /**
   * Silently discard all staged chunks without sending anything to Anam.
   *
   * Called in endOfAiResponse() when containsMessageNotSent() returns true.
   * Unlike flushQueue(), this does not touch the serial queue — any prior
   * committed audio (from a previous response) continues uninterrupted.
   */
  const discardStagedChunks = useCallback(() => {
    stagedChunksRef.current = [];
    resamplerRef.current.reset();
  }, []);

  return {
    // Mode A — auto-streaming
    enqueueChunk,
    enqueueEndSequence,
    flushQueue,

    // Mode B — staged commit (current mode)
    stageChunk,
    commitStagedChunks,
    discardStagedChunks
  };
}
